import _asyncToGenerator from "/builds/unifygroup/uno/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/asyncToGenerator";
import _regeneratorRuntime from "/builds/unifygroup/uno/app/node_modules/next/dist/compiled/@babel/runtime/regenerator";
import compact from 'lodash/compact';
import isString from 'lodash/isString';
import uniq from 'lodash/uniq';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { mapAsync } from '@uno/common-shared';
import { tagOptionsToValues } from '../utils';

/** Hook to manage a collection of selectable options and convert from ids to TagOption and vice versa
 *
 * Warning: If the initialOptions change, the added tagOptions in the state are not persisted, as the initial options are considered the source of truth.
 * To persist added options, save them at a higher level.
 */
var useTagOptions = function useTagOptions(initialOptions, initialValues, parameters) {
  var _ref = parameters !== null && parameters !== void 0 ? parameters : {},
      createOption = _ref.createOption,
      _ref$allowMultipleSel = _ref.allowMultipleSelection,
      allowMultipleSelection = _ref$allowMultipleSel === void 0 ? false : _ref$allowMultipleSel,
      _ref$removeNotFoundVa = _ref.removeNotFoundValues,
      removeNotFoundValues = _ref$removeNotFoundVa === void 0 ? function () {
    return undefined;
  } : _ref$removeNotFoundVa;

  var intl = useIntl();

  var _useState = useState(initialOptions),
      availableTagOptions = _useState[0],
      setAvailableTagOptions = _useState[1];

  var _useState2 = useState(initialValues),
      selectedValues = _useState2[0],
      setSelectedValues = _useState2[1];

  var getTagOptionFromTitle = useCallback(function (title) {
    return availableTagOptions.find(function (tagOption) {
      return tagOption.title === title;
    });
  }, [availableTagOptions]);
  var getTagOptionFromId = useCallback(function (id) {
    return availableTagOptions.find(function (tagOption) {
      return tagOption.id === id;
    });
  }, [availableTagOptions]);
  useEffect(function () {
    removeNotFoundValues(selectedValues, function (value) {
      return getTagOptionFromId(value) !== undefined;
    }); // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedOptionsState cannot be in dependencies to avoid infinite loops
  }, [getTagOptionFromId, removeNotFoundValues]);
  var valuesToTagOptions = useCallback(function (values) {
    return values.length ? values.map(function (value) {
      var _availableTagOptions$;

      return (_availableTagOptions$ = availableTagOptions.find(function (tagOption) {
        return tagOption.id === value;
      })) !== null && _availableTagOptions$ !== void 0 ? _availableTagOptions$ : {
        id: value,
        title: intl.formatMessage({
          id: 'tagAutocomplete.invalidValue'
        }, {
          value: value
        })
      };
    }) : [];
  }, [availableTagOptions, intl]);

  var runCreateOption = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(title) {
      var existingOption, newOption;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              existingOption = getTagOptionFromTitle(title);

              if (!existingOption) {
                _context.next = 3;
                break;
              }

              return _context.abrupt("return", existingOption);

            case 3:
              if (!(createOption !== undefined)) {
                _context.next = 9;
                break;
              }

              _context.next = 6;
              return createOption(title);

            case 6:
              _context.t0 = _context.sent;
              _context.next = 10;
              break;

            case 9:
              _context.t0 = {
                id: Math.random().toString(36).replace('0.', "".concat(title, "-")),
                title: title
              };

            case 10:
              newOption = _context.t0;
              // We need to update availableTagOptions so the value can be correctly displayed instead of "value not found..."
              setAvailableTagOptions(function (prevValue) {
                return prevValue.concat(newOption);
              });
              return _context.abrupt("return", newOption);

            case 13:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function runCreateOption(_x) {
      return _ref2.apply(this, arguments);
    };
  }();

  var newTagOptionsToValues = /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(updatedSelectedTagOptions) {
      var tagOptions, finalValues;
      return _regeneratorRuntime.wrap(function _callee3$(_context3) {
        while (1) {
          switch (_context3.prev = _context3.next) {
            case 0:
              if (!(!allowMultipleSelection && updatedSelectedTagOptions.length > 1)) {
                _context3.next = 2;
                break;
              }

              return _context3.abrupt("return", selectedValues);

            case 2:
              _context3.next = 4;
              return mapAsync(updatedSelectedTagOptions, /*#__PURE__*/function () {
                var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(option) {
                  return _regeneratorRuntime.wrap(function _callee2$(_context2) {
                    while (1) {
                      switch (_context2.prev = _context2.next) {
                        case 0:
                          if (!isString(option)) {
                            _context2.next = 4;
                            break;
                          }

                          _context2.next = 3;
                          return runCreateOption(option);

                        case 3:
                          return _context2.abrupt("return", _context2.sent);

                        case 4:
                          if (!(option.inputValue !== undefined)) {
                            _context2.next = 8;
                            break;
                          }

                          _context2.next = 7;
                          return runCreateOption(option.inputValue);

                        case 7:
                          return _context2.abrupt("return", _context2.sent);

                        case 8:
                          return _context2.abrupt("return", option);

                        case 9:
                        case "end":
                          return _context2.stop();
                      }
                    }
                  }, _callee2);
                }));

                return function (_x3) {
                  return _ref4.apply(this, arguments);
                };
              }());

            case 4:
              tagOptions = _context3.sent;
              finalValues = tagOptionsToValues(uniq(compact(tagOptions))); // update selectedValues so the new values can be displayed in the field

              setSelectedValues(finalValues);
              return _context3.abrupt("return", finalValues);

            case 8:
            case "end":
              return _context3.stop();
          }
        }
      }, _callee3);
    }));

    return function newTagOptionsToValues(_x2) {
      return _ref3.apply(this, arguments);
    };
  }();

  return {
    availableTagOptions: availableTagOptions,
    selectedValues: selectedValues,
    selectedTagOptions: valuesToTagOptions(selectedValues),
    newTagOptionsToValues: newTagOptionsToValues
  };
};

export default useTagOptions;